import { Inmate } from '@reliance/types/models/Inmate';
import { InmateActivity } from '@reliance/types/models/InmateActivity';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import { createColumnHelper } from '@tanstack/react-table';
import { formatCurrency } from '../../format/Currency';
import { useInmateActivitiesByInmateId } from '../../hooks/InmateActivity';
import { useInmateAccountSummary } from '../../hooks/InmateAccountSummary';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import AddActivityForm from '../../inmate-activity/AddActivityForm';
import { INMATE_ACTIVITY_TYPE_FILTER_OPTIONS } from '../../inmate-activity/inmateActivityTypeFilterOptions';
import PhoneCardReverseModal from '../../inmate-activity/PhoneCardReverseModal';
import SummariesTable from '../../inmate-account-payment/SummariesTable';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import { useServerTableFilterState } from '../../table/useServerTableFilterState';
import { formatPhoneNumber, toE164 } from '../../format/PhoneNumber';

const COLUMNS = [
  { id: 'id', meta: { sortName: 'id' } },
  { id: 'dateFinalized', meta: { sortName: 'dateFinalized' } },
  { id: 'type', meta: { sortName: 'type' } },
  { id: 'amount', meta: { sortName: 'amount' } },
  { id: 'phonecard', meta: { sortName: 'phonecard' } },
  { id: 'description', meta: { sortName: 'description' } },
  { id: 'runningBalance', meta: { sortName: 'runningBalance' } },
];

const DEFAULT_DATE_RANGE_YEARS = 5;
const DEFAULT_PER_PAGE = 50;
const ACTIVITY_ORDER_BY = '-dateFinalized';

type InmateMessagingAccountProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
  onInmateUpdate?: () => void;
};

const InmateMessagingAccount: React.FC<InmateMessagingAccountProps> = (props) => {
  const {
    inmate,
    tabSelected,
    refreshTrigger,
    onInmateUpdate,
  } = props;

  const [activeSubTab, setActiveSubTab] = useState<string>('transactions');
  const [reverseConfirmActivity, setReverseConfirmActivity] = useState<InmateActivity | null>(null);

  const startDate = useMemo(
    () => moment().subtract(DEFAULT_DATE_RANGE_YEARS, 'year').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    []
  );
  const endDate = useMemo(
    () => moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    []
  );

  const baseRecord = useMemo(
    () =>
      ({
        inmateId: inmate.id,
        startDate,
        endDate,
        orderBy: ACTIVITY_ORDER_BY,
      }) as Record<string, unknown>,
    [inmate.id, startDate, endDate]
  );
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: DEFAULT_PER_PAGE },
    initialSorting: [{ id: 'dateFinalized', desc: true }],
  });
  const activityFilters = useMemo(
    () => ({
      ...tableState.mergedRecord,
      inmateId: inmate.id,
      orderBy: (tableState.mergedRecord.orderBy as string | undefined) ?? ACTIVITY_ORDER_BY,
    }),
    [inmate.id, tableState.mergedRecord]
  );

  const activitiesResult = useInmateActivitiesByInmateId(null, activityFilters) as {
    result?: { data: InmateActivity[]; total: number; supportingData?: { previousBalance?: number } };
    loading: boolean;
    execute: (id: number) => void;
  };

  const monthlySummaryResult = useInmateAccountSummary(
    tabSelected && inmate.id ? inmate.id : null,
    'Monthly'
  );
  const dailySummaryResult = useInmateAccountSummary(
    tabSelected && inmate.id ? inmate.id : null,
    'Daily'
  );

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        activitiesResult.execute(inmate.id);
        monthlySummaryResult.execute(inmate.id);
        dailySummaryResult.execute(inmate.id);
      }
    }, [activitiesResult, monthlySummaryResult, dailySummaryResult, inmate.id]),
  });

  useEffect(() => {
    if (tabSelected && inmate.id) {
      activitiesResult.execute(inmate.id);
    }
  }, [tabSelected, inmate.id, tableState.mergedRecord]);

  const isLoading =
    activitiesResult.loading ||
    (activeSubTab === 'monthly' && monthlySummaryResult.loading) ||
    (activeSubTab === 'daily' && dailySummaryResult.loading);
  useNotifyTabLoading('messaging-account', isLoading);

  const handleActivityAdded = useCallback(() => {
    if (inmate.id) {
      activitiesResult.execute(inmate.id);
      onInmateUpdate?.();
    }
  }, [inmate.id, activitiesResult, onInmateUpdate]);

  const onPhoneCardReverseModalClose = useCallback(
    (saved: boolean) => {
      setReverseConfirmActivity(null);
      if (saved && inmate.id) {
        void activitiesResult.execute(inmate.id);
        onInmateUpdate?.();
      }
    },
    [inmate.id, activitiesResult, onInmateUpdate]
  );

  const walletBalance = inmate.messagingBalance?.[0]?.balance;
  const trustBalance = inmate.trustAccountBalance;
  const previousBalance = activitiesResult.result?.supportingData?.previousBalance ?? 0;
  const activityData = activitiesResult.result?.data ?? [];
  const activityTotal = activitiesResult.result?.total ?? 0;
  const perPage = tableState.pagination.pageSize;
  const totalPages = Math.ceil(activityTotal / perPage) || 1;
  const page = tableState.pagination.pageIndex + 1;
  const orderBy = (tableState.mergedRecord.orderBy as string | undefined) ?? ACTIVITY_ORDER_BY;

  const activityRows = useMemo(() => {
    const data = [...activityData];
    const sortAsc = orderBy === 'dateCreated' || orderBy === 'dateCreated,asc';
    if (sortAsc && data.length > 0) {
      let balance = previousBalance;
      data.forEach((row) => {
        balance += parseFloat(String((row as InmateActivity & { amount: number }).amount));
        (row as InmateActivity & { runningBalance?: number }).runningBalance = balance;
      });
    }
    return data;
  }, [activityData, orderBy, previousBalance]);

  const columnHelper = createColumnHelper<InmateActivity & { runningBalance?: number }>();
  const activityColumns = useMemo(
    () => [
      columnHelper.accessor('id', {
        id: 'id',
        header: 'ID',
        meta: { sortName: 'id' },
      }),
      columnHelper.accessor((row) => row.dateFinalized, {
        id: 'dateFinalized',
        header: 'Date',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return '';
          const d = typeof val === 'object' && val && 'date' in val ? (val as { date: string }).date : String(val);
          return moment(d).format('MM/DD/YYYY HH:mm:ss');
        },
        meta: { sortName: 'dateFinalized' },
      }),
      columnHelper.accessor((row) => row.type, {
        id: 'type',
        header: 'Type',
        cell: (info) => {
          const row = info.row.original;
          const payment = row.payment;
          return (
            <>
              <span>{String(row.type)}</span>
              {payment && (
                <div className="small">
                  {payment.customerName && <span>{payment.customerName}</span>}
                  {payment.customerAni && (
                    <br />
                  )}
                  {payment.customerAni && (
                    <Link to={`/account/${encodeURIComponent(toE164(payment.customerAni))}`}>
                      {formatPhoneNumber(payment.customerAni)}
                    </Link>
                  )}
                </div>
              )}
            </>
          );
        },
        meta: {
          sortName: 'type',
          filterComponent: 'SelectFilter',
          filterOptions: INMATE_ACTIVITY_TYPE_FILTER_OPTIONS,
        },
      }),
      columnHelper.accessor((row) => row.amount, {
        id: 'amount',
        header: 'Amount',
        cell: (info) => formatCurrency(Number(info.getValue())),
        meta: { sortName: 'amount' },
      }),
      columnHelper.accessor((row) => row.phonecard, {
        id: 'phonecard',
        header: 'Phonecard',
        cell: (info) => {
          const row = info.row.original;
          const isPhonecardLoad = String(row.type) === 'Phonecard Load';
          const reversed = row.reversed;
          return (
            <>
              <span>{info.getValue() ?? ''}</span>
              {isPhonecardLoad && !reversed && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger ms-1"
                  onClick={() => setReverseConfirmActivity(row)}>
                  Reverse Load
                </button>
              )}
            </>
          );
        },
        meta: { sortName: 'phonecard' },
      }),
      columnHelper.accessor((row) => row.description, {
        id: 'description',
        header: 'Description',
        meta: { sortName: 'description' },
      }),
      columnHelper.accessor((row) => (row as InmateActivity & { runningBalance?: number }).runningBalance, {
        id: 'runningBalance',
        header: 'Running Balance',
        cell: (info) => {
          const v = info.getValue();
          return v != null ? formatCurrency(v) : '';
        },
      }),
    ],
    [columnHelper]
  );

  const monthlyRows = useMemo(() => {
    const raw = monthlySummaryResult.result ?? [];
    return [...raw].reverse();
  }, [monthlySummaryResult.result]);

  const dailyRows = useMemo(() => {
    const raw = dailySummaryResult.result ?? [];
    return [...raw].reverse();
  }, [dailySummaryResult.result]);

  return (
    <div className="jarviswidget">
      <div className="row">
        <div className="col-md-12">
          <h5>Wallet balance: {walletBalance != null ? formatCurrency(walletBalance) : ''}</h5>
          {trustBalance != null && (
            <h5>Trust account balance: {formatCurrency(trustBalance)}</h5>
          )}

          <Tabs
            activeKey={activeSubTab}
            onSelect={(k) => setActiveSubTab(k ?? 'transactions')}>
            <Tab eventKey="transactions" title="Transactions">
              <div className="jarviswidget">
                <div className="content">
                  <div className="mb-3">
                    <AddActivityForm
                      inmateId={inmate.id}
                      accountType="wallet"
                      onSuccess={handleActivityAdded} />
                  </div>
                  <BaseHtmlTable
                    rows={activityRows}
                    columnDefs={activityColumns}
                    page={page}
                    totalPages={totalPages}
                    total={activityTotal}
                    perPage={perPage}
                    serverPagination={tableState.serverPagination}
                    onServerPaginationChange={tableState.onServerPaginationChange}
                    serverSorting={tableState.serverSorting}
                    onServerSortingChange={tableState.onServerSortingChange}
                    serverColumnFilters={tableState.serverColumnFilters}
                    onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
                    paginationMode="server"
                    loading={activitiesResult.loading}
                    pageSizeOptions={[50, 100, 200, 500]} />
                </div>
              </div>
            </Tab>
            <Tab eventKey="monthly" title="Monthly Summary">
              <div className="jarviswidget">
                <div className="content">
                  <SummariesTable
                    rows={monthlyRows}
                    summaryType="monthly"
                    loading={monthlySummaryResult.loading}
                  />
                </div>
              </div>
            </Tab>
            <Tab eventKey="daily" title="Daily Summary">
              <div className="jarviswidget">
                <div className="content">
                  <SummariesTable
                    rows={dailyRows}
                    summaryType="daily"
                    loading={dailySummaryResult.loading}
                  />
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      <PhoneCardReverseModal
        show={reverseConfirmActivity != null}
        activity={reverseConfirmActivity}
        onClose={onPhoneCardReverseModalClose}
      />
    </div>
  );
};

export default InmateMessagingAccount;
